import { GroupCondition } from '@/api/enums';
import { group, like } from '@/api/helpers';
import { FiltersType, ListingRequest } from '@/api/types';
import { client } from '@/client';
import { User } from '@/client/users';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account';
import { AppChip } from '@/ui/chip';
import { FlexContainer } from '@/ui/styled-ui';
import { DropdownProps } from 'primereact/dropdown';
import React, { HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { LazyLoadingSelect } from '../generic/LazyLoadingSelect';

const StyledItem = styled(FlexContainer)`
  p {
    color: var(--black-main);
    margin: 0;
    font-weight: 600;
    white-space: nowrap;
  }

  span {
    color: var(--gray-darker);
  }
`;

type UsersSelectInputProps = {
  onChange: (value: User | null) => void;
  onBlur?: () => void;
  additionalFilters?: FiltersType;
  selectedValue?: User | null;
  isDisabled?: boolean;
  placeholder?: string;
  accountId?: string;
  tooltip?: string;
  inputId?: string;
} & Omit<
  HTMLAttributes<HTMLSelectElement>,
  'onChange' | 'onBlur' | 'defaultChecked'
>;

export const UsersSelectInput: React.FC<UsersSelectInputProps> = ({
  onChange,
  onBlur,
  className,
  additionalFilters,
  selectedValue,
  isDisabled,
  placeholder,
  accountId,
  tooltip,
  inputId,
}) => {
  const { t } = useTranslation();
  const currentAccount = useAppSelector(selectCurrentAccount);

  const fetchUsers = isDisabled
    ? undefined
    : async (params: ListingRequest) =>
        await client.users.getAccountUsers(
          { ...params, sort: ['active,desc', 'firstName,asc'] },
          accountId || currentAccount?.id,
        );

  const itemTemplate = (user: User) => (
    <StyledItem justify="space-between" gap={8}>
      <FlexContainer justify="flex-start" gap={8} width="auto">
        <p>{user.name}</p>
        <span>{user.email}</span>
      </FlexContainer>
      <AppChip
        label={user.active ? t('generic.active') : t('generic.inactive')}
        type={user.active ? 'primary' : 'secondary'}
        size="s"
      />
    </StyledItem>
  );

  return (
    <LazyLoadingSelect
      inputId={inputId}
      dataKey="id"
      fetchService={fetchUsers}
      filter
      showClear
      optionLabel="name"
      itemTemplate={itemTemplate}
      valueTemplate={(user: User | null, props: DropdownProps) =>
        user ? user.name ?? user.email : props.placeholder
      }
      optionFilter={(value) => [
        group(GroupCondition.OR, [like('name', value), like('email', value)]),
      ]}
      filterPlaceholder={t('user.search.name.email')}
      placeholder={placeholder || t('generic.select')}
      additionalFilters={additionalFilters}
      selectedValue={selectedValue ?? undefined}
      className={className}
      handleOnChange={(value: User | null) => onChange(value ?? null)}
      onBlur={onBlur}
      tooltip={tooltip}
      tooltipOptions={{ position: 'right', showOnDisabled: true }}
      isDisabled={isDisabled}
    />
  );
};
